import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, RefreshCw, Shield } from 'lucide-react'; // Shield icon add kora hoyeche

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Access Manage korar jonno notun states
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [userPermissions, setUserPermissions] = useState({});

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    role: 'Technician',
    full_name: '',
  });

  const [newPassword, setNewPassword] = useState('');

  // Sidebar Menu List
  const MENU_LIST = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'assets', label: 'Assets' },
    { id: 'customers', label: 'Customers' },
    { id: 'history', label: 'History' },
    { id: 'reports', label: 'Reports' },
    { id: 'performance', label: 'Performance' },
    { id: 'alerts', label: 'Alerts' },
    { id: 'users', label: 'Users' },
    { id: 'settings', label: 'Settings' }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, {
          email: formData.email,
          role: formData.role,
          full_name: formData.full_name,
          active: formData.active,
        });
        toast.success('User updated successfully!');
      } else {
        await api.post('/users', formData);
        toast.success('User created successfully!');
      }
      setShowDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      email: user.email || '',
      role: user.role,
      full_name: user.full_name,
      active: user.active,
    });
    setShowDialog(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post(`/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword,
      });
      toast.success('Password reset successfully!');
      setShowPasswordDialog(false);
      setNewPassword('');
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error('Failed to reset password');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { active: !user.active });
      toast.success(`User ${!user.active ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      toast.error('Failed to update user status');
    }
  };

  // Access Access control handlers
  const handleEditAccessClick = (user) => {
    setSelectedUser(user);
    setUserPermissions(user.permissions || {});
    setShowAccessDialog(true);
  };

  const handleCheckboxChange = (menuId) => {
    setUserPermissions((prev) => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleSavePermissions = async () => {
    try {
      await api.put(`/users/${selectedUser.id}/permissions`, {
        permissions: userPermissions
      });
      toast.success(`Access updated for ${selectedUser.username}!`);
      setShowAccessDialog(false);
      fetchUsers(); // List update korar jonne
    } catch (error) {
      console.error('Failed to update permissions:', error);
      toast.error('Failed to save permissions.');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      email: '',
      role: 'Technician',
      full_name: '',
    });
    setEditingUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="users-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">User Management</h1>
          <p className="text-lg text-muted-foreground">Manage system users and permissions</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                resetForm();
                setShowDialog(true);
              }}
              data-testid="add-user-button"
              size="lg"
              className="h-14 px-8 font-bold uppercase tracking-wide shadow-lg shadow-primary/20"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Username *</Label>
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    disabled={editingUser}
                    className="h-12"
                  />
                </div>
                {!editingUser && (
                  <div>
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingUser}
                      minLength={6}
                      className="h-12"
                    />
                  </div>
                )}
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    className="h-12"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-12"
                  />
                </div>
                <div>
                  <Label>Role *</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })} required>
                    <SelectTrigger className="h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="Service Admin">Service Admin</SelectItem>
                      <SelectItem value="Technician">Technician</SelectItem>
                      <SelectItem value="Second Checker">Second Checker</SelectItem>
                      <SelectItem value="Management">Management</SelectItem>
                      <SelectItem value="Sales">Sales</SelectItem>
                      <SelectItem value="Warehouse">Warehouse</SelectItem>
                      <SelectItem value="Accounts">Accounts</SelectItem>
                      <SelectItem value="Auditor">Auditor</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Service Admin = Technician + can add/edit customer names
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-user-button">
                  {editingUser ? 'Update' : 'Create'} User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Username</th>
                <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Full Name</th>
                <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Email</th>
                <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Role</th>
                <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((user) => (
                <tr key={user.id} data-testid={`user-row-${user.username}`} className="hover:bg-muted/50 transition-colors">
                  <td className="px-6 py-4 font-bold">{user.username}</td>
                  <td className="px-6 py-4">{user.full_name}</td>
                  <td className="px-6 py-4">{user.email || '-'}</td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-bold">{user.role}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={user.active}
                        onCheckedChange={() => handleToggleActive(user)}
                        data-testid={`toggle-active-${user.username}`}
                      />
                      <span className="text-sm">{user.active ? 'Active' : 'Inactive'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        title="Edit User Info"
                        onClick={() => handleEdit(user)}
                        data-testid={`edit-user-${user.username}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        title="Reset Password"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowPasswordDialog(true);
                        }}
                        data-testid={`reset-password-${user.username}`}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      {/* Access Edit Button Add kora holo */}
                      <Button
                        variant="outline"
                        size="sm"
                        title="Edit Page Access"
                        onClick={() => handleEditAccessClick(user)}
                      >
                        <Shield className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Reset password for <strong>{selectedUser?.full_name}</strong>?
            </p>
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                className="h-12"
                data-testid="new-password-input"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleResetPassword} data-testid="confirm-reset-password">
                Reset Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Access Control (Permissions) Dialog Add Kora Holo */}
      <Dialog open={showAccessDialog} onOpenChange={setShowAccessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Edit Access</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Tick the menus you want <strong>{selectedUser?.username}</strong> to view:
            </p>
            
            <div className="space-y-2 border rounded-md p-4 max-h-60 overflow-y-auto">
              {MENU_LIST.map((menu) => (
                <label key={menu.id} className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-gray-300 text-primary cursor-pointer"
                    checked={!!userPermissions[menu.id]}
                    onChange={() => handleCheckboxChange(menu.id)}
                  />
                  <span className="font-medium text-sm">{menu.label}</span>
                </label>
              ))}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="outline" onClick={() => setShowAccessDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSavePermissions}>
                Save Access
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default UsersPage;